// BattleFaceQuoteForm.jsx
// 
// BATTLEFACE TRAVEL INSURANCE QUOTE APPLICATION
// =============================================
// 
// This React component provides a complete travel insurance quote-to-purchase flow
// integrated with BattleFace API and Stripe payment processing.
//
// FLOW OVERVIEW:
// Step 0 (Details)    → API: createQuote()      → Step 1 (Quote Review)
// Step 1 (Quote)      → API: processOrder()     → Step 2 (Order Info)  
// Step 2 (Order)      → API: Payment Process    → Step 3 (Payment)
// Step 3 (Payment)    → API: confirmQuote()     → Step 4 (Confirmation)
//
// KEY FEATURES:
// • Inter font for modern, professional appearance
// • Form validation with user-friendly error messages
// • Phone number auto-formatting and email validation
// • Trip cancellation coverage dropdown (2500, 5000, 10000, 20000, 30000)
// • Coverage amount resets to 2500 when returning to details step
// • Middle name omitted from API if empty (optimization)
// • Stripe payment integration following BattleFace diagram
// • Comprehensive error handling and loading states
// • Responsive Material-UI design
//
// PAYMENT INTEGRATION:
// Follows BattleFace/Stripe integration pattern:
// 1. Create PaymentMethod with Stripe
// 2. Fetch PaymentIntent from BattleFace (GET /orders/{id}/payment/intent/)
// 3. Confirm payment with Stripe using client_secret
// 4. Handle alternative flow if client_secret unavailable
//
// The component uses Material-UI for styling and Stripe for payment processing.

import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, MenuItem, Typography, CircularProgress, Alert, Stepper, Step, StepLabel, StepIcon,
  Divider, ThemeProvider, createTheme, Checkbox, FormControlLabel, Dialog, DialogActions, DialogContent, 
  DialogContentText, DialogTitle, Snackbar, useMediaQuery, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { Download, ExpandMore, HelpOutline, ArrowBack } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Import theme configuration
import { THEMES } from './themes/themeConfig';
import defaultTheme from './themes/defaultTheme';
import orangeTheme from './themes/orangeTheme';
import FireworksOverlay from './FireworksOverlay';



// Load Stripe public key from environment variables (supports Vite and CRA)
const backendBase = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5262';
const stripePromise = loadStripe(process.env?.REACT_APP_STRIPE_PUBLIC_KEY);
const BATTLEFACE_API_TOKEN = process.env?.REACT_APP_BATTLEFACE_API_TOKEN;
const APP_PRODUCT_URL = process.env?.REACT_APP_PRODUCT_URL;


// Stripe CardElement styling to match our Inter font theme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
};

// PaymentForm Component - Handles the Stripe payment process
// This component is wrapped by Stripe Elements and handles the final payment step.
// It follows the BattleFace/Stripe integration flow:
// 1. Create a PaymentMethod with Stripe using card details
// 2. Fetch PaymentIntent from BattleFace (GET request, no body)
// 3. Use client_secret from BattleFace to confirm payment with Stripe
// 4. Handle success or error responses
const PaymentForm = ({ 
  quote, 
  tripCostCoverage, 
  numTravelers, 
  orderNumber, 
  applicants,
  onPaymentSuccess, 
  onPaymentError, 
  loading, 
  setLoading 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState('');
  
  // Terms and conditions state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [destinationDisclosureAccepted, setDestinationDisclosureAccepted] = useState(false);

  const handlePaymentSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setPaymentError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError('Card element not found. Please refresh and try again.');
      return;
    }

    setLoading(true);
    setPaymentError('');

    try {
      // Get main applicant's name for billing details
      const mainApplicant = applicants?.find(app => app.is_main) || applicants?.[0] || {};
      const customerName = `${mainApplicant.first_name || ''} ${mainApplicant.last_name || ''}`.trim() || 'Customer';

      // Step 1a: Create PaymentMethod via Stripe API
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: customerName,
          email: mainApplicant.email || '',
          phone: mainApplicant.contact_number?.replace(/\D/g, '') || '', // Send digits only
        },
      });

      if (paymentMethodError) {
        setPaymentError(paymentMethodError.message);
        onPaymentError(paymentMethodError.message);
        return;
      }

      // console.log('PaymentMethod created:', paymentMethod.id);

      // Step 1b: Fetch PaymentIntent from BattleFace API
      // This follows the BattleFace/Stripe integration diagram:
      // - Use GET request (no body) to retrieve payment intent
      // - Extract client_secret from response for Stripe confirmation
      let clientSecret = null;
      
      try {
        const paymentIntentResponse = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderNumber}/payment/intent/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': BATTLEFACE_API_TOKEN
          }
          // Note: No body for GET request
        });

        if (paymentIntentResponse.ok) {
          const paymentIntentData = await paymentIntentResponse.json();
          // console.log('BattleFace PaymentIntent Response:', paymentIntentData);

          // Extract the client_secret from the response
          clientSecret = paymentIntentData.client_secret || 
                        paymentIntentData.data?.client_secret || 
                        paymentIntentData.intent?.client_secret;
        } else {
          // console.log('PaymentIntent endpoint not available, will use alternative flow');
        }
      } catch (intentError) {
        // console.log('PaymentIntent fetch failed, will use alternative flow:', intentError.message);
      }

      // Step 2: Confirm payment with Stripe using the client_secret from BattleFace
      // This is the standard Stripe payment confirmation flow when we have a client_secret
      if (clientSecret) {
        // console.log('Using client_secret:', clientSecret);

        // Use Stripe's confirmCardPayment with the PaymentMethod and client_secret
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: paymentMethod.id
          }
        );

        if (confirmError) {
          setPaymentError(confirmError.message);
          onPaymentError(confirmError.message);
        } else if (paymentIntent.status === 'succeeded') {
          // console.log('Payment succeeded:', paymentIntent.id);
          onPaymentSuccess(paymentIntent);
        } else {
          setPaymentError(`Payment status: ${paymentIntent.status}`);
          onPaymentError(`Payment status: ${paymentIntent.status}`);
        }
      } else {
        // Alternative flow: BattleFace may have processed payment without returning client_secret
        throw new Error("ClientSecret is not set.");
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error.message || 'An unexpected error occurred';
      setPaymentError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePaymentSubmit}>
      <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius={2}>
        <Typography variant="h6" mb={2}>Payment Details</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Secure payment processing powered by Stripe
        </Typography>
        
        <Box mb={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
          <CardElement options={cardElementOptions} />
        </Box>

        {paymentError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {paymentError}
          </Alert>
        )}

        {/* Terms & Conditions Section */}
        <Box mb={3}>
          <Typography variant="h6" mb={2} fontWeight="bold">
            TERMS & CONDITIONS
          </Typography>
          
          {/* Electronic Signature Checkbox */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  sx={{ alignSelf: 'flex-start', mt: 0 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  Checking here constitutes my electronic signature. I confirm that I have read, understand 
                  and agree to the{' '}
                  <Box 
                    component="span" 
                    sx={{ 
                      textDecoration: 'underline', 
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontWeight: 'bold'
                    }}
                    onClick={() => {
                      // Open terms and conditions in new tab/window
                      window.open('https://partner.battleface.com/library/landsman-elite-multi-annual', '_blank');
                    }}
                  >
                    policy terms and conditions
                  </Box>
                  , all &nbsp;
                  
                                    <Box 
                    component="span" 
                    sx={{ 
                      textDecoration: 'underline', 
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontWeight: 'bold'
                    }}
                    onClick={() => {
                      // Open terms and conditions in new tab/window
                      window.open('https://www.battleface.com/documents/important-notices-and-disclosures-spi.pdf', '_blank');
                    }}
                  >
                    important disclosures
                  </Box>, 
                  which includes fraud warnings, privacy notice, and consent to electronic signature and delivery.
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
          </Box>

          {/* Destination Disclosure Checkbox */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={destinationDisclosureAccepted}
                  onChange={(e) => setDestinationDisclosureAccepted(e.target.checked)}
                  sx={{ alignSelf: 'flex-start', mt: 0 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    By checking here, I understand that the Multi-Trip Annual Plan will not cover 
                    me for travel to certain destinations.{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        textDecoration: 'underline', 
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 'medium'
                      }}
                      onClick={() => {
                        // Open link in new tab/window
                        window.open('https://library.battleface.com/ineligible-destinations/', '_blank');
                      }}
                    >
                      View the full list of destinations for which we do not offer coverage.
                    </Box>
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </Box>

        <Button 
          type="submit" 
          variant="contained" 
          color="success" 
          fullWidth 
          disabled={!stripe || loading || !termsAccepted || !destinationDisclosureAccepted}
          sx={{ mt: 2 }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              Processing Payment...
            </Box>
          ) : (
            `Pay $${quote?.data?.quoted_price?.toFixed(2) || '0.00'}`
          )}
        </Button>
      </Box>
    </form>
  );
};


const COUNTRY_LIST = [
  { code: 'US', name: 'United States' }
];

const STATE_LIST = [
  

];

// Define the 5-step process for travel insurance quote and purchase
const steps = [
  'Details',      // Step 0: Collect trip and traveler information
  'Quote',        // Step 1: Display quote with coverage options and pricing
  'Review Order', // Step 2: Collect applicant details and create order
  'Payment',      // Step 3: Process payment through Stripe/BattleFace
  'Download'      // Step 4: Show confirmation and policy details
];

// Custom StepIcon component that shows a download icon for the final step
// Only shows download icon when actively on step 5, otherwise shows number 5
const CustomStepIcon = (props) => {
  const { icon, active, completed } = props;
  
  // Show download icon only for step 5 (icon === 5) AND when it's the active step
  if (icon === 5 && active) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText'
        }}
      >
        <Download sx={{ fontSize: 16 }} />
      </Box>
    );
  }
  
  // Use default StepIcon for all other steps (including step 5 when not active)
  return <StepIcon {...props} />;
};

// Helper function to calculate date of birth from age
// BattleFace API requires DOB, but our form collects age for simplicity
// Uses January 1st as default birth date for the calculated year
const calculateDateOfBirth = (age) => {
  const today = new Date();
  const birthYear = today.getFullYear() - age;
  return `${birthYear}-01-01`; // Format: YYYY-MM-DD
};

// Helper function to clean applicant data for API submission
// Removes middle_name field if empty (API optimization)
// Transforms form data into the format expected by BattleFace API
const cleanApplicantData = (applicant, index, travelerAges, coverageAmount) => {
  const cleanedData = {
    first_name: applicant.first_name,
    last_name: applicant.last_name,
    email: applicant.email,
    contact_number: applicant.contact_number || '',
    address: applicant.address || '',
    city: applicant.city || '',
    post_code: applicant.post_code,
    country_code: applicant.country_code,
    state_code: applicant.state_code,
    is_main: applicant.is_main,
    dob: calculateDateOfBirth(parseInt(travelerAges[index])),
    trip_cost: coverageAmount
  };

  // Only include middle_name if it's not empty
  if (applicant.middle_name && applicant.middle_name.trim() !== '') {
    cleanedData.middle_name = applicant.middle_name;
  }

  return cleanedData;
};

// Helper function to format phone number
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const digitsOnly = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedDigits = digitsOnly.substring(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (limitedDigits.length >= 6) {
    return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
  } else if (limitedDigits.length >= 3) {
    return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
  } else if (limitedDigits.length > 0) {
    return `(${limitedDigits}`;
  }
  return '';
};

// Helper function to validate email format
const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return { isValid: false, message: 'Email required' };
  }

  const trimmedEmail = email.trim().toLowerCase();
  
  // Basic format validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }
  
  // Length validation
  if (trimmedEmail.length < 5) {
    return { isValid: false, message: 'Email address is too short' };
  }
  
  if (trimmedEmail.length > 254) {
    return { isValid: false, message: 'Email address is too long' };
  }
  
  // Additional format checks
  if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.') || 
      trimmedEmail.startsWith('@') || trimmedEmail.endsWith('@')) {
    return { isValid: false, message: 'Invalid email format' };
  }
  
  if (trimmedEmail.includes('..') || trimmedEmail.includes('@.') || trimmedEmail.includes('.@')) {
    return { isValid: false, message: 'Invalid email format' };
  }
  
  // Check for valid domain
  const parts = trimmedEmail.split('@');
  if (parts.length !== 2) {
    return { isValid: false, message: 'Email must contain exactly one @' };
  }
  
  const [localPart, domain] = parts;
  if (localPart.length === 0 || domain.length === 0) {
    return { isValid: false, message: 'Email must have both username and domain' };
  }
  
  if (!domain.includes('.')) {
    return { isValid: false, message: 'Email domain must contain a dot' };
  }
  
  const domainParts = domain.split('.');
  if (domainParts.some(part => part.length === 0)) {
    return { isValid: false, message: 'Invalid domain format' };
  }
  
  return { isValid: true, message: '' };
};

// Helper function to find the first allowable date
const findFirstAllowableDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 180);
  
  // Check if tomorrow is within the allowable range
  if (tomorrow <= maxDate) {
    return tomorrow;
  }
  
  // If somehow tomorrow is beyond max date, return null
  return null;
};

// Helper function to get Travel Medical coverage amount based on state
// New Hampshire (NH) has a lower coverage limit of $50,000
// All other states have the standard $250,000 coverage
const getTravelMedicalCoverage = (stateCode) => {
  if (stateCode === 'NH') {
    return { amount: '$50,000', value: 50000 };
  }
  return { amount: '$250,000', value: 250000 };
};

// Main BattleFaceQuoteForm Component
// This is the primary React component that manages the entire quote-to-purchase flow.
// The component uses a stepper UI pattern with 5 steps:
// 0: Details - Collect trip information and traveler details
// 1: Quote - Display quote results and coverage options  
// 2: Review Order - Collect applicant personal information
// 3: Payment - Process payment through Stripe/BattleFace
// 4: Download - Show confirmation and policy details
export default function BattleFaceQuoteForm() {
  // Theme state
  const [themeName, setThemeName] = useState(THEMES.ORANGE);
  const themeMap = {
    [THEMES.DEFAULT]: defaultTheme,
    [THEMES.ORANGE]: orangeTheme,
  };
  const theme = themeMap[themeName] || defaultTheme;
  // Step management - tracks which step of the process the user is on
  const [activeStep, setActiveStep] = useState(0);

  // Read marketing / affiliate / session parameters from URL
  const [amc, setAmc] = useState(null); // affiliate marketing code
  const [mcc, setMcc] = useState(null); // marketing channel code (required)
  const [mccAuthResult, setMccAuthResult] = useState(null); // store authorization response
  const [mccAuthError, setMccAuthError] = useState(null);

  // Marketing channel enforcement flag (missing OR inactive OR not yet authorized)
  const hasValidStates = Array.isArray(mccAuthResult?.availableStates) && mccAuthResult.availableStates.length > 0;
  const isMccMissing = (mcc === null) || (mccAuthResult && (mccAuthResult.IsMarketingChannelActive === false || !hasValidStates));
  // Dynamic state options from marketing channel (fallback to static list)
  const stateOptions = mccAuthResult?.availableStates?.length
    ? mccAuthResult.availableStates.map(s => ({ code: s.code, name: s.name }))
        : STATE_LIST;    

  // Guarded step setter to prevent advancing past step 0 when MCC missing
  const guardedSetActiveStep = (next) => {
    if (isMccMissing && next > 0) return; // block progression
    setActiveStep(next);
  };
  useEffect(() => {
    // Parse theme and other params from query param
    const params = new URLSearchParams(window.location.search);
    const themeParam = params.get('theme');
    if (themeParam && Object.values(THEMES).includes(themeParam.toLowerCase())) {
      setThemeName(themeParam.toLowerCase());
    }
    const amcParam = params.get('amc');
    if (amcParam) {
      setAmc(amcParam);
      //console.log(`AMC parameter detected: ${amcParam}`);
    }
    const mccParam = params.get('mcc');
    if (mccParam && mccParam.trim() !== '') {
      setMcc(mccParam.trim());
    } else {
      setMcc(null); // explicitly null if missing or empty
    }

    // Call marketing channel authorization early if present
    (async () => {
      try {
        if (mccParam && mccParam.trim() !== '') {
          const authData = await AuthorizeMarketingChannel(mccParam.trim());
          setMccAuthResult(authData || null);
          if (!authData) {
            setMccAuthError('Marketing channel could not be authorized.');
          }
        }
      } catch (e) {
        setMccAuthError(e.message || 'Failed to authorize marketing channel.');
      }
    })();

    const quoteIdParam = params.get('quoteid') || params.get('quoteId');
    if (quoteIdParam) {
      // Fetch quote data from Azure tracking endpoint
      (async () => {
        try {         

          const res = await RetrieveQuote(quoteIdParam);          
          
          if(res == "")
          {
            console.log("No quote found for the provided quoteId");
          }

              var originalResponse = res;                     
              
              setQuote({data: { quoted_price: originalResponse.quoted_price || 0}});
              // Set applicants state if present in the response

              setHomeCountryState(originalResponse.host_country_state);           
              setTripCostCoverage(originalResponse.details[0].package_alias.split('_')[0] || 2500);
              
              if (originalResponse.applicants) {
                                
                originalResponse.applicants[0].first_name = '';
                originalResponse.applicants[0].last_name = '';
                originalResponse.applicants[0].middle_name = '';
                originalResponse.applicants[0].email = '';
                originalResponse.applicants[0].country_code = 'US';
                originalResponse.applicants[0].contact_number = '';
                originalResponse.applicants[0].address = '';
                originalResponse.applicants[0].city = '';
                originalResponse.applicants[0].post_code = '';                
                originalResponse.applicants[0].state_code = originalResponse.host_country_state;

                setApplicants(originalResponse.applicants);
                setNumTravelers(originalResponse.applicants.length);
                // Set travelerAges from applicants' dob if available
                const ages = originalResponse.applicants.map(app => {
                  if (app.dob) {
                    // Expecting dob in YYYY-MM-DD format
                    const birthYear = parseInt(app.dob.split('-')[0], 10);
                    const thisYear = new Date().getFullYear();
                    return (thisYear - birthYear).toString();
                  }
                  return '';
                });
                setTravelerAges(ages);                              
              }
              // Extract quoteId from the returned data (try multiple fields)
              const extractedQuoteId = res.quoteId || res.id || res.quote_id ||
                res.data?.id || originalResponse.quote_id || originalResponse.quote_number || quoteIdParam;
              setQuoteId(extractedQuoteId);
              setActiveStep(1); // Move to Quote Review step
            } catch (error) {
              console.error('Error fetching quote data:', error);
            }          
          })();
    }

    // Check for policy download parameters first
    const policyNumberParam = params.get('policyNumber') || params.get('policyNumberParam');
    const orderIdParam = params.get('orderid') || params.get('orderId');

    // If both policy number and order ID are present, only download policy (skip order rehydration)
    if (policyNumberParam && orderIdParam) {
      // Automatically trigger policy download
      (async () => {
        try {
          console.log('Attempting automatic policy download for policy:', policyNumberParam, 'order:', orderIdParam);

          // Call the BattleFace API to get the file URL
          const response = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderIdParam}/policies/${policyNumberParam}/files`, {
            method: 'GET',
            headers: {
              'Authorization': BATTLEFACE_API_TOKEN
            }
          });

          console.log('Files API response status:', response.status);

          if (!response.ok) {
            throw new Error(`Failed to get policy file URL (Status: ${response.status}). Policy files may not be ready yet.`);
          }

          const data = await response.json();
          console.log('Files API response data:', data);

          // Check if we have a valid response with file data
          if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
            throw new Error('No policy files found in response.');
          }

          // Get the first file's URL
          const fileUrl = data.data[0].url;
          if (!fileUrl) {
            throw new Error('No download URL found in file data.');
          }

          console.log('Using file URL for automatic download:', fileUrl);

          // Fetch the actual file using the provided URL
          const fileResponse = await fetch(fileUrl, {
            method: 'GET',
            headers: {
              'Authorization': BATTLEFACE_API_TOKEN
            }
          });

          if (!fileResponse.ok) {
            throw new Error(`Failed to download file from URL (Status: ${fileResponse.status}).`);
          }

          // Create a blob from the file response and trigger download
          const blob = await fileResponse.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `policy-${policyNumberParam}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);

          console.log('Policy download completed successfully');
        } catch (error) {
          console.error('Automatic policy download error:', error);
          setError(`Failed to download policy: ${error.message}. Please try again later or contact support.`);
        }
      })();
    } else if (orderIdParam) {
      // Only rehydrate order if policyNumber is NOT present
      // Fetch quote data from Azure tracking endpoint
      (async () => {
        try {         

          const res = await RetrieveOrder(orderIdParam);          

          if(res == "")
          {
            console.log("No order found for the provided orderId");
          }

              var originalResponse = res;       

              setQuote({data: { quoted_price: originalResponse.premium || 0}});

              const extractedQuoteId = res.quoteId || res.id || res.quote_id ||
                res.data?.id || originalResponse.quote_id || originalResponse.quote_number;

                setQuoteId(extractedQuoteId);
                setOrderNumber(orderIdParam);
                setTripCostCoverage(originalResponse.items[0].details[0].detail.split('_')[0] || 2500);

                // Set trip dates based on whether original date has passed
                const firstAllowableDate = findFirstAllowableDate();
                let startDate = firstAllowableDate; // default to tomorrow
                let endDate = new Date(firstAllowableDate);
                endDate.setDate(endDate.getDate() + 364);

                // Check if we have a start date from the order
                if (originalResponse.start_at) {
                  const originalStartDate = new Date(originalResponse.start_at);

                  // If the original start date is in the future or today, use it
                  if (originalStartDate >= firstAllowableDate) {
                    startDate = originalStartDate;
                    // Recalculate end date to be 364 days from this start date
                    endDate = new Date(originalStartDate);
                    endDate.setDate(endDate.getDate() + 364);
                  }
                  // Otherwise, use the firstAllowableDate (already set as default)
                }

                setTripStart(startDate);
                setTripEnd(endDate);

                // Set applicants and related data from order
                if (originalResponse.applicant) {
                  // Order has a single applicant object, need to convert to array
                  const applicant = originalResponse.applicant;

                  // Set home country state from applicant
                  if (applicant.state_code) {
                    setHomeCountryState(applicant.state_code);
                  }

                  // Create applicants array from the single applicant
                  setApplicants([{
                    first_name: applicant.first_name || '',
                    middle_name: applicant.middle_name || '',
                    last_name: applicant.last_name || '',
                    email: applicant.email || '',
                    contact_number: applicant.contact_number || '',
                    address: applicant.address || '',
                    city: applicant.city || '',
                    post_code: applicant.post_code || '',
                    country_code: applicant.country_code || 'US',
                    state_code: applicant.state_code || homeCountryState,
                    is_main: true
                  }]);

                  setNumTravelers(1);

                  // Set traveler age from applicant's dob if available
                  if (applicant.dob) {
                    // Expecting dob in YYYY-MM-DD format
                    const birthYear = parseInt(applicant.dob.split('-')[0], 10);
                    const thisYear = new Date().getFullYear();
                    const age = (thisYear - birthYear).toString();
                    setTravelerAges([age]);
                  }
                } else if (originalResponse.applicants && originalResponse.applicants.length > 0) {
                  // Order has applicants array
                  const firstApplicant = originalResponse.applicants[0];

                  // Set home country state from first applicant
                  if (firstApplicant.state_code) {
                    setHomeCountryState(firstApplicant.state_code);
                  }

                  setApplicants(originalResponse.applicants.map(app => ({
                    first_name: app.first_name || '',
                    middle_name: app.middle_name || '',
                    last_name: app.last_name || '',
                    email: app.email || '',
                    contact_number: app.contact_number || '',
                    address: app.address || '',
                    city: app.city || '',
                    post_code: app.post_code || '',
                    country_code: app.country_code || 'US',
                    state_code: app.state_code || homeCountryState,
                    is_main: app.is_main || false
                  })));

                  setNumTravelers(originalResponse.applicants.length);

                  // Set traveler ages from applicants' dob if available
                  const ages = originalResponse.applicants.map(app => {
                    if (app.dob) {
                      // Expecting dob in YYYY-MM-DD format
                      const birthYear = parseInt(app.dob.split('-')[0], 10);
                      const thisYear = new Date().getFullYear();
                      return (thisYear - birthYear).toString();
                    }
                    return '';
                  });
                  setTravelerAges(ages);
                }

                // Don't call confirmQuote here - that happens after payment
                // Just go to the payment step
                setActiveStep(3); // Move to Payment step
            } catch (error) {
              console.error('Error fetching order data:', error);
            }          
          })();
    }



  }, []);



  // User input states - stores form data entered by the user
  const [tripStart, setTripStart] = useState(null);           // Trip start date
  const [tripEnd, setTripEnd] = useState(null);               // Trip end date (auto-calculated)
  const [homeCountryState, setHomeCountryState] = useState(''); // User's home state
  const [numTravelers, setNumTravelers] = useState(1);        // Number of travelers (1-10)
  const [travelerAges, setTravelerAges] = useState(['']);     // Array of traveler ages
  
  // State for the ineligible state dialog
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [selectedIneligibleState, setSelectedIneligibleState] = useState('');
  const [tripCostCoverage, setTripCostCoverage] = useState(2500); // Selected coverage amount (single source of truth)

  // API and UX states - manages API responses and loading states
  const [loading, setLoading] = useState(false);              // Loading indicator
  const [quote, setQuote] = useState(null);                   // Quote response from BattleFace
  const [quoteId, setQuoteId] = useState(null);               // Quote ID for subsequent API calls
  const [orderNumber, setOrderNumber] = useState(null);       // Order number from BattleFace
  const [confirmation, setConfirmation] = useState(null);     // Final confirmation response
  const [error, setError] = useState('');                     // Error messages
  const [paymentIntent, setPaymentIntent] = useState(null);   // Stripe payment result

  // Session tracking (per browser tab). Each new tab/window gets a fresh session id.
  // Logic:
  // 1. If a sid param is provided (?sid=...), use it (allows external correlation).
  // 2. Else, generate a new UUID and store in sessionStorage (tab-scoped).
  // 3. On errors or missing APIs, fallback to a timestamp/random based id.
  const [sessionId, setSessionId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sidParam = params.get('sid') || params.get('session') || params.get('sessionId');
      if (sidParam && sidParam.length > 5) {
        try { sessionStorage.setItem('bf_session_id', sidParam); } catch {}
        return sidParam;
      }
      // Always generate new per-tab (ignore any previous value). Use sessionStorage for potential reuse on reload.
      let newId;
      if (window.crypto?.randomUUID) {
        newId = window.crypto.randomUUID();
      } else {
        newId = 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      }
      try { sessionStorage.setItem('bf_session_id', newId); } catch {}
      return newId;
    } catch {
      return 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
  });

  // Email dialog state for sending quote/order reference to applicant
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailing, setEmailing] = useState(false);
  // Snackbar for professional feedback messages
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenEmailDialog = () => setOpenEmailDialog(true);
  const handleCloseEmailDialog = () => {
    if (!emailing) setOpenEmailDialog(false);
  };

  const handleConfirmEmail = async () => {
    setEmailing(true);
    const mainApplicant = applicants.find(a => a.is_main) || applicants[0];
    const recipient = mainApplicant?.email?.trim();
    if (!recipient) {
      showSnackbar('No applicant email provided. Please enter an email address on the applicant form.', 'warning');
      setEmailing(false);
      return;
    }
    try {
      // Compose email content (HTML + plain text fallback)
      const applicantName = [mainApplicant.first_name, mainApplicant.last_name].filter(Boolean).join(' ') || 'Customer';
      const quotedPrice = `$${quote?.data?.quoted_price?.toFixed(2) || '0.00'}`;
      const cancellationLimit = `$${tripCostCoverage?.toLocaleString()}`;
      const orderRef = orderNumber || 'N/A';
      const subject = `Annual Travel Protection Quote - Reference: ${orderRef}`;

      const textBody = [
        `Hello ${applicantName},`,
        '',
        'Thank you for requesting a BattleFace annual plan quote.',
        `Order Reference: ${orderRef}`,
        `Quoted Price: ${quotedPrice}`,
        `Trip Cancellation Limit: ${cancellationLimit}`,
        'If you have not completed payment yet, you can return to the site to finish your purchase.',
        '',
        'This message was generated from the quote application.'
      ].join('\n');

    const planCostDisplay = `${quote?.data?.currency_id || 'USD'} ${quote?.data?.quoted_price?.toFixed(2) || '0.00'}`;
    const coverageLimitDisplay = `$${tripCostCoverage?.toLocaleString()}`;
    const tripInterruptionDisplay = `150% of ${coverageLimitDisplay}`;
    const htmlBody = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Annual Travel Protection Plan</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f5f5f5;">
    <tr>
      <td align="center" style="padding: 20px;">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; max-width: 600px;">
          
          <!-- Greeting and Intro -->
          <tr>
            <td style="padding: 20px 20px 16px; font-size: 15px; color: #333333;">
              Hello ${applicantName},
            </td>
          </tr>
          <tr>
            <td style="padding: 0 20px 16px; font-size: 14px; line-height: 1.55; color: #333333;">
              Thank you for requesting your BattleFace annual plan quote. Below is a summary of your selection and reference details. Keep this email for your records or complete your purchase when ready.
            </td>
          </tr>
          <tr>
            <td style="padding: 0 20px 20px; font-size: 12px; color: #777777;">
              Order Reference: <strong>${orderRef}</strong>
            </td>
          </tr>
          
          <!-- Plan Header -->
          <tr>
            <td style="padding: 0 20px 20px;">
              <table width="100%" cellpadding="20" cellspacing="0" border="0" style="border: 2px solid #ed7c1c; border-radius: 8px; background-color: #ffffff;">
                <tr>
                  <td width="60%" style="vertical-align: top;">
                    <div style="color: #666666; font-size: 14px; font-weight: bold; margin-bottom: 5px;">Total Plan Cost:</div>
                    <div style="color: #999999; font-size: 13px;">Coverage for 365 days</div>
                  </td>
                  <td width="40%" style="text-align: right; vertical-align: top;">
                    <div style="color: #ed7c1c; font-size: 32px; font-weight: bold; margin-bottom: 5px;">${planCostDisplay}</div>
                    <div style="color: #ed7c1c; font-size: 13px; text-decoration: underline;">
                      <a href="https://partner.battleface.com/library/landsman-elite-multi-annual" target="_blank" style="color: #ed7c1c; text-decoration: underline;">View Sample Plan</a>
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Section Header -->
          <tr>
            <td style="padding: 20px; text-align: center;">
              <div style="color: #333333; font-size: 16px; font-weight: bold;">Included in your plan</div>
            </td>
          </tr>
          
          <!-- Benefits Header -->
          <tr>
            <td style="padding: 15px 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td width="60%" style="color: #ed7c1c; font-size: 16px; font-weight: bold;">Benefits</td>
                  <td width="40%" style="text-align: right; color: #999999; font-size: 12px;">Maximum Limits<br>per plan term</td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Trip Cancellation -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Trip Cancellation</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">${coverageLimitDisplay}</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Coverage for your pre-paid, non-refundable trip cost and some related expenses if you need to cancel a trip for a covered reason.
              </div>
            </td>
          </tr>
          
          <!-- Trip Interruption -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Trip Interruption</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">${tripInterruptionDisplay}</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Coverage in the event a portion of a trip is missed, or if you must return home early due to unforeseen circumstances, including illness, injury, or death.
              </div>
            </td>
          </tr>
          
          <!-- Single Occupancy -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Single Occupancy</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">Up to ${coverageLimitDisplay}</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Coverage for your additional costs if your traveling companion cancels or interrupts their trip for a covered reason and you choose to still go.
              </div>
            </td>
          </tr>
          
          <!-- Trip Delay -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Trip Delay</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">$200/day up to $2,000</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Protection against expenses incurred as a result of travel delay for a defined period of time. Examples include unexpected hotel, meal, or transportation costs.
              </div>
            </td>
          </tr>
          
          <!-- Missed Connection -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Missed Connection</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">$1,000</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Can reimburse you for the cost of extra transportation to rejoin the trip, additional local expenses, and the cost of missed arrangements.
              </div>
            </td>
          </tr>
          
          <!-- Baggage Loss -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Baggage Loss</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">$500/item up to $2,000</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Coverage for reimbursement of lost, stolen, or damaged luggage, sporting equipment and personal effects.
              </div>
            </td>
          </tr>
          
          <!-- Baggage Delay -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Baggage Delay</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">$100/day up to $1,000</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Reimburses the cost of clothing, toiletries, and other essential items if your baggage is delayed for at least 12 hours.
              </div>
            </td>
          </tr>
          
          <!-- Travel Medical -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Travel Medical</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">$250,000</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Coverage for the costs to treat a medical emergency, including COVID-19, during a trip.
              </div>
            </td>
          </tr>
          
          <!-- Emergency Dental (sub-benefit) -->
          <tr>
            <td style="padding: 15px 20px 15px 40px; border-bottom: 1px solid #eeeeee; background-color: #fafafa;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Emergency Dental</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">$750</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Coverage for emergency dental care while on your trip.
              </div>
            </td>
          </tr>
          
          <!-- Hospital Room & Board (sub-benefit) -->
          <tr>
            <td style="padding: 15px 20px 15px 40px; border-bottom: 1px solid #eeeeee; background-color: #fafafa;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Hospital Room & Board</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">unlimited</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Can reimburse up to the daily amount shown for hospital room & board.
              </div>
            </td>
          </tr>
          
          <!-- Emergency Evacuation -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Emergency Evacuation</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">$500,000</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                Coverage to transport you to the nearest adequate medical facility, or home, in the event of a medical emergency during a covered trip. Primary coverage, plus includes coverage for Adventure Activities.
              </div>
            </td>
          </tr>

          <!-- Medical & Travel Assistance Services -->
          <tr>
            <td style="padding: 20px; border-bottom: 1px solid #eeeeee;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="color: #333333; font-size: 14px; font-weight: bold; padding-bottom: 8px;">Medical & Travel Assistance Services</td>
                  <td style="text-align: right; color: #666666; font-size: 14px; font-weight: bold; white-space: nowrap; padding-bottom: 8px;">Included</td>
                </tr>
              </table>
              <div style="color: #666666; font-size: 13px; line-height: 1.5;">
                The purchase of an Annual Multi-Trip Plan includes both Emergency Assistance and general Travel Assistance. Non Insurance Assistance services are provided by Robin Assist. Robin Assist is battleface's brand name for providing 24/7 responsive customer service, claims, emergency travel and medical assistance from any device, any time, any place. Their operations team consists of multi-lingual talented humans who come from extensive travel protection service backgrounds. In the event you become sick or injured while traveling, services provided with your plan can help. battleface will get you started with a referral to a local licensed medical professional. They will monitor your condition and, if medically necessary, make arrangements to transport you to the nearest medical facility that can properly treat you. With Emergency Evacuation provided under the Annual Multi-Trip plan, they can even pay eligible medical transportation costs up front, including getting you home. Please review the FAQ section for additional services provided.
              </div>
            </td>
          </tr>

          <!-- CTA Button -->
          <tr>
            <td style="padding: 20px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="center" style="background-color: #ed7c1c; border-radius: 8px; padding: 16px;">
                    <a href="${APP_PRODUCT_URL}/?mcc=${mcc}&orderId=${orderRef}" style="color: #ffffff; font-size: 16px; font-weight: bold; text-decoration: none; display: block;">Proceed to Order</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

      // POST to Azure Function endpoint
      const endpoint = `${backendBase}/Data/SendEmail`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: recipient, subject: subject, body: htmlBody })
      });

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        const msg = `Failed to send email (status ${response.status}). ${bodyText || ''}`.trim();
        console.error(msg);
        showSnackbar(msg, 'error');
      } else {
        showSnackbar('Quote email queued for delivery.', 'success');
      }
    } catch (err) {
      console.error('Email send error:', err);
      showSnackbar(`Error sending email: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setEmailing(false);
      setOpenEmailDialog(false);
    }
  };

  // Marketing page toggle
  const [showMarketing, setShowMarketing] = useState(false);
  const toggleMarketing = () => setShowMarketing(prev => !prev);

  // Celebration and responsiveness
  const isNarrow = useMediaQuery('(max-width:390px)');
  const showCelebration = activeStep === 4 && !!confirmation;

  // Applicant information states - stores personal details for all travelers
  const [applicants, setApplicants] = useState([{
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    contact_number: '',
    address: '',
    city: '',
    post_code: '',
    country_code: 'US',
    state_code: '',
    is_main: true  // First applicant is always the main applicant
  }]);

  // Effect: Update all applicants' state when homeCountryState changes
  // This ensures consistency across all travelers' addresses
  useEffect(() => {
    if (homeCountryState) {
      setApplicants(prev => prev.map(applicant => ({
        ...applicant,
        state_code: homeCountryState
      })));
    }
  }, [homeCountryState]);

  // Set initial trip start date to first allowable date
  useEffect(() => {
    if (!tripStart) {
      const firstAllowableDate = findFirstAllowableDate();
      if (firstAllowableDate) {
        setTripStart(firstAllowableDate);
        // Automatically set trip end date to 364 days after start date
        const endDate = new Date(firstAllowableDate);
        endDate.setDate(endDate.getDate() + 364);
        setTripEnd(endDate);
      }
    }
  }, [tripStart]);

  // Validation
  const errors = {};
  if (!tripStart) errors.tripStart = 'Plan start date required';
  if (!homeCountryState) errors.homeCountryState = 'Home state required';
  if (numTravelers < 1 || numTravelers > 10) errors.numTravelers = '1-10 travelers allowed';
  travelerAges.forEach((age, idx) => {
    if (age !== '' && (isNaN(age) || parseInt(age) < 0 || parseInt(age) > 120)) {
      errors[`age${idx}`] = 'Enter valid age (0-120)';
    } else if (age === '') {
      errors[`age${idx}`] = 'Age required';
    }
  });

  // Validate applicant information for step 2 (Order step)
  if (activeStep === 2) {
    applicants.forEach((applicant, idx) => {
      if (!applicant.first_name) errors[`applicant${idx}_first_name`] = 'First name required';
      if (!applicant.last_name) errors[`applicant${idx}_last_name`] = 'Last name required';
      
      // Enhanced email validation using helper function
      const emailValidation = validateEmail(applicant.email);
      if (!emailValidation.isValid) {
        errors[`applicant${idx}_email`] = emailValidation.message;
      }
      
      if (!applicant.post_code) errors[`applicant${idx}_post_code`] = 'Postal code required';
      if (!applicant.contact_number) {
        errors[`applicant${idx}_contact_number`] = 'Phone number required';
      } else {
        // Remove all non-digits to validate
        const digitsOnly = applicant.contact_number.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
          errors[`applicant${idx}_contact_number`] = 'Phone number must be 10 digits';
        }
      }
    });
  }

  // Handlers
  const handleNumTravelersChange = (e) => {
    const n = parseInt(e.target.value, 10);
    setNumTravelers(n);
    setTravelerAges((prev) => {
      const arr = [...prev];
      while (arr.length < n) arr.push('');
      while (arr.length > n) arr.pop();
      return arr;
    });
    
    // Update applicants array to match number of travelers
    setApplicants((prev) => {
      const arr = [...prev];
      while (arr.length < n) {
        arr.push({
          first_name: '',
          middle_name: '',
          last_name: '',
          email: '',
          contact_number: '',
          address: '',
          city: '',
          post_code: '',
          country_code: 'US',
          state_code: homeCountryState,
          is_main: arr.length === 0 // First applicant is main
        });
      }
      while (arr.length > n) arr.pop();
      return arr;
    });
  };

  const handleAgeChange = (idx, value) => {
    setTravelerAges((prev) => {
      const arr = [...prev];
      arr[idx] = value;
      return arr;
    });
  };

  const handleApplicantChange = (idx, field, value) => {
    setApplicants((prev) => {
      const arr = [...prev];
      
      // Format phone number if it's the contact_number field
      if (field === 'contact_number') {
        value = formatPhoneNumber(value);
      }
      
      // Trim whitespace for email field
      if (field === 'email') {
        value = value.trim().toLowerCase();
      }
      
      arr[idx] = { ...arr[idx], [field]: value };
      return arr;
    });
  };

  // API calls - These functions handle communication with BattleFace API
  
  /**
   * STEP 0 → STEP 1: Create Initial Quote
   * Purpose: Get pricing from BattleFace based on trip details and traveler ages
   * API: POST /api/partner/quote
  // API Functions - These handle communication with BattleFace API

  /**
   * STEP 1: Create Initial Quote
   * Called when user submits the Details form (step 0)
   * Sends trip information to BattleFace API to get pricing quote
   * Input: Trip dates, traveler ages, coverage amount, location
   * Output: Quote with pricing and quote ID for next steps
   */

    async function AuthorizeMarketingChannel(marketingChannelCode) {
        try {
            const productCode = 'BattleFace Annual Trip Travel Protection';
            const url = `${backendBase}/Data/authenticate?marketingChannelCode=${encodeURIComponent(marketingChannelCode)}&productCode=${encodeURIComponent(productCode)}`;

            const res = await fetch(url);
            if (res.ok) {
                const json = await res.json();

                if (json) {

                    return json;
                }
            }
        } catch (err) {
            // Optionally handle error
            console.error('Failed to fetch quote by quoteId from URL', err);
            return "";
        }
    }   

    async function SaveOrder(logData, orderId) {
      try {

        console.log("Saving order log data", JSON.stringify(logData));

          await fetch(`${backendBase}/Data/SaveOrder?orderId=${orderId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json','x-functions-key': 'NhXZQstdl3Svj1XoC8adbtFjP-u5tjujvAcw_5VMWV8KAzFuuOKO5w==' },
            body: JSON.stringify(logData),
          });

      } catch (err) {
        // Optionally handle logging errors (do not block user)
        console.error('Failed to SaveOrder', err);
      }
    }

    async function GetPolicyDocuments(policyNumber, orderNumber, transactionId)
    {
                      try {

                        // console.log('Attempting to get policy file URL for policy:', policyNumber, 'order:', orderNumber);

                        // Call the new API endpoint to get the file URL
                        const response = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderNumber}/policies/${policyNumber}/files`, {
                          method: 'GET',
                          headers: {
                            'Authorization': BATTLEFACE_API_TOKEN
                          }
                        });

                        // console.log('Files API response status:', response.status);

                        if (!response.ok) {
                          throw new Error(`Failed to get policy file URL (Status: ${response.status}). Policy files may not be ready yet.`);
                        }

                        const data = await response.json();
                        // console.log('Files API response data:', data);

                        // Check if we have a valid response with file data
                        if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
                          throw new Error('No policy files found in response.');
                        }

                              await SaveDocuments({
                                endpoint: 'documents',
                                marketing_channel_code: mcc,
                                quote_id: transactionId,        
                                session_id: sessionId,
                                request: orderNumber,
                                response: data,
                                status: response?.status,
                                timestamp: new Date().toISOString(),
                              }, orderNumber);      

                      } catch (error) {
                        console.error('Policy download error:', error);                    
                      }
    }

    async function SaveDocuments(logData, orderId) {
      try {

        console.log("Saving order log data", JSON.stringify(logData));

          await fetch(`${backendBase}/Data/SaveDocuments?orderId=${orderId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json','x-functions-key': 'NhXZQstdl3Svj1XoC8adbtFjP-u5tjujvAcw_5VMWV8KAzFuuOKO5w==' },
            body: JSON.stringify(logData),
          });

      } catch (err) {
        // Optionally handle logging errors (do not block user)
        console.error('Failed to SaveDocuments', err);
      }
    }

    async function SaveQuote(logData, quoteId) {
        try {          

            console.log(logData);

          await fetch(`${backendBase}/Data/SaveQuote?quoteId=${quoteId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json','x-functions-key': 'NhXZQstdl3Svj1XoC8adbtFjP-u5tjujvAcw_5VMWV8KAzFuuOKO5w==' },
            body: JSON.stringify(logData),
          });

      } catch (err) {
        // Optionally handle logging errors (do not block user)
        console.error('Failed to SaveQuote', err);
      }
    }

    /**
     * Log Battleface API errors to the backend for troubleshooting.
     * This function is called when any Battleface API call fails.
     * @param {Object} errorData - Error details to log
     * @param {string} errorData.endpoint - The Battleface API endpoint that was called
     * @param {string} errorData.httpMethod - HTTP method used (GET, POST, etc.)
     * @param {number} errorData.httpStatusCode - HTTP status code returned (0 for network errors)
     * @param {string} errorData.errorMessage - The error message or description
     * @param {string} errorData.requestPayload - The request payload sent (JSON string)
     * @param {string} errorData.responsePayload - The response body received (JSON string)
     * @param {string} errorData.additionalContext - Any additional context
     */
    async function LogBattlefaceError(errorData) {
      try {
        const payload = {
          sessionId: sessionId || null,
          marketingChannelCode: mcc || null,
          endpoint: errorData.endpoint,
          httpMethod: errorData.httpMethod || 'POST',
          httpStatusCode: errorData.httpStatusCode || 0,
          errorMessage: errorData.errorMessage,
          requestPayload: errorData.requestPayload || null,
          responsePayload: errorData.responsePayload || null,
          widgetName: 'AnnualTripProtection',
          userAgent: navigator.userAgent,
          additionalContext: errorData.additionalContext || null
        };

        await fetch(`${backendBase}/Data/LogError`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-functions-key': 'NhXZQstdl3Svj1XoC8adbtFjP-u5tjujvAcw_5VMWV8KAzFuuOKO5w==' 
          },
          body: JSON.stringify(payload),
        });
      } catch (err) {
        // Don't block user if error logging fails - just log to console
        console.error('Failed to log Battleface error:', err);
      }
    }

    async function PaymentConfirmed(logData, orderId) {
      try {
              

          await fetch(`${backendBase}/Data/PaymentConfirmed?orderId=${orderId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json','x-functions-key': 'NhXZQstdl3Svj1XoC8adbtFjP-u5tjujvAcw_5VMWV8KAzFuuOKO5w==' },
            body: JSON.stringify(logData),
          });

      } catch (err) {
        // Optionally handle logging errors (do not block user)
        console.error('Failed to SaveOrder', err);
      }
    }    

    async function RetrieveOrder(orderid) {
    try {
      
        const url = `${backendBase}/Data/GetOrder?orderId=${orderid}&code=NhXZQstdl3Svj1XoC8adbtFjP-u5tjujvAcw_5VMWV8KAzFuuOKO5w==`;
      
      const res = await fetch(url);
              if (res.ok) {
                const json = await res.json();
                if (json && json.response) {

                  var originalResponse = json.response;    
                  setSessionId(json.session_id);   
                
                  return originalResponse.data;
                }
              }
            } catch (err) {
              // Optionally handle error
              console.error('Failed to fetch quote by quoteId from URL', err);
              return "";
            }
    }
    
    async function RetrieveQuote(quoteId) {
    try {

        const url = `${backendBase}/Data/GetQuote?quoteId=${quoteId}&code=NhXZQstdl3Svj1XoC8adbtFjP-u5tjujvAcw_5VMWV8KAzFuuOKO5w==`;
                  
      const res = await fetch(url);
              if (res.ok) {
                const json = await res.json();
                if (json && json.response) {

                  var originalResponse = json.response;  

                  setSessionId(json.session_id);                  

                  return originalResponse.data;
                }
              }
            } catch (err) {
              // Optionally handle error
              console.error('Failed to fetch quote by quoteId from URL', err);
              return "";
            }
    }        


  const createQuote = async () => {
    if (isMccMissing) {
      setError('Missing marketing channel (mcc). Cannot create quote.');
      return;
    }
    setLoading(true); 
    setError('');
    
    try {
      // Build the payload according to BattleFace API requirements
      const payload = {
        "start_date": tripStart ? tripStart.toISOString().split('T')[0] : "2025-06-29", // Convert to YYYY-MM-DD format
        "end_date": tripEnd ? tripEnd.toISOString().split('T')[0] : "2025-07-04",       // Auto-calculated end date
        "applicants": travelerAges.map((age, index) => ({            
            "is_main": index === 0,                                    // First traveler is main applicant
            "dob": calculateDateOfBirth(parseInt(age)),                // Convert age to date of birth
            "trip_cost": tripCostCoverage                              // Use selected coverage amount
        })),
        "product": 1303,                                               // BattleFace product ID (could be configurable)
        "host_country": "US",                                          // Always US for this implementation
        "host_country_state": homeCountryState || "TX",               // User's selected state
        "currency": "USD",                                             // Always USD for this implementation
        "destinations": ["US"],                                        // Trip destinations (domestic US travel)
        "packages": [tripCostCoverage.toString() + "_limit"]          // Coverage package (e.g., "2500_limit")
      };

      let res, data, extractedQuoteId;

      // Send quote request to BattleFace API
      try {
          res = await fetch('https://gateway.battleface.com/api/partner/quote', {
            method: 'POST',
              headers: { 
                'Content-Type': 'application/json', 
                Authorization: BATTLEFACE_API_TOKEN 
              },
            body: JSON.stringify(payload),
          });

      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: 'https://gateway.battleface.com/api/partner/quote',
          httpMethod: 'POST',
          httpStatusCode: res.status,
          errorMessage: `Failed to create quote: HTTP ${res.status}`,
          requestPayload: JSON.stringify(payload),
          responsePayload: responseText,
          additionalContext: 'createQuote function'
        });
        throw new Error('Failed to create quote');
      }
          data = await res.json();

      setQuote(data);

      // Extract quote ID from response (API may return it in different locations)
      extractedQuoteId = data.quote_number || data.id || data.quote_id || 
                               data.data?.id || data.data?.quote_id || data.data?.quote_number;

      if (!extractedQuoteId) {
        throw new Error('No quote ID found in API response');
      }

      setQuoteId(extractedQuoteId); 
  guardedSetActiveStep(1); // Move to Quote Review step (guarded)

          // ...existing code...
        } finally {

      await SaveQuote({
        endpoint: 'quote',
        quote_id: extractedQuoteId,
        marketing_channel_code: mcc,
        session_id: sessionId,
        request: extractedQuoteId,
        response: data,
        status: res?.status,
        timestamp: new Date().toISOString(),
      }, extractedQuoteId);          

      }      

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * STEP 1: Update Quote with New Coverage Amount
   * Purpose: Recalculate pricing when user changes trip cancellation coverage
   * API: POST /api/partner/quote (same as initial quote but with new coverage)
   * Input: Same as createQuote but with updated coverage amount
   * Output: Updated quote with new pricing
   */
  /**
   * STEP 1 VARIANT: Requote with Different Coverage Amount
   * Called when user changes coverage amount in the dropdown on step 1
   * Gets updated pricing for the new coverage level
   * Input: New coverage amount (2500, 5000, 10000, 20000, or 30000)
   * Output: Updated quote with new pricing
   */
  const requoteWithNewCoverage = async (newCoverageAmount) => {
    if (isMccMissing) {
      setError('Missing marketing channel (mcc). Cannot requote.');
      return;
    }
    setTripCostCoverage(newCoverageAmount);
    setLoading(true); 
    setError('');
    
    try {
      // Build payload with updated coverage amount - same structure as initial quote
      const payload = {
        "start_date": tripStart ? tripStart.toISOString().split('T')[0] : "2025-06-29",
        "end_date": tripEnd ? tripEnd.toISOString().split('T')[0] : "2025-07-04",    
        "applicants": travelerAges.map((age, index) => ({
            "is_main": index === 0,
            "dob": calculateDateOfBirth(parseInt(age)),
            "trip_cost": newCoverageAmount                              // Updated with new coverage amount
        })),
        "product": 1303,
        "host_country": "US",
        "host_country_state": homeCountryState || "TX",
        "currency": "USD",
        "destinations": ["US"],
        "packages": [newCoverageAmount.toString() + "_limit"]          // Updated package (e.g., "5000_limit")
      };
    
      // Send requote request to BattleFace API (same endpoint as initial quote)
      const res = await fetch('https://gateway.battleface.com/api/partner/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: 'https://gateway.battleface.com/api/partner/quote',
          httpMethod: 'POST',
          httpStatusCode: res.status,
          errorMessage: `Failed to get new quote: HTTP ${res.status}`,
          requestPayload: JSON.stringify(payload),
          responsePayload: responseText,
          additionalContext: 'requoteWithNewCoverage function'
        });
        throw new Error('Failed to get new quote');
      }
      const data = await res.json();

      setQuote(data);

      // Update quote ID if it changed (may be same or different)
      const extractedQuoteId = data.quote_number || data.id || data.quote_id || 
                               data.data?.id || data.data?.quote_id || data.data?.quote_number;

      if (extractedQuoteId) {
        setQuoteId(extractedQuoteId);
      }      

      await SaveQuote({
        endpoint: 'quote',
        quote_id: extractedQuoteId,
        marketing_channel_code: mcc,
        session_id: sessionId,
        request: extractedQuoteId,
        response: data,
        status: res?.status,        
        timestamp: new Date().toISOString(),
      }, extractedQuoteId);     


    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * STEP 4: Confirm Quote/Policy After Payment
   * Called after successful payment to finalize the insurance policy
   * Currently uses GET request but may need adjustment based on API docs
   * Input: Quote ID from initial quote and applicant details collected in step 2
   * Output: Policy confirmation with policy number
   * Note: This is called automatically after successful payment
   */
  const confirmQuote = async (passedQuoteId, passedOrderNumber) => {
    const idToUse = passedQuoteId || quoteId;
    const orderToUse = passedOrderNumber || orderNumber;
    if (!idToUse) {
      setError('No quote ID available. Please get a quote first.');
      return;
    }
    if (!orderToUse) {
      setError('No order number available. Please create an order first.');
      return;
    }
    let data, res;
    setLoading(true); setError('');
    try {
      // POST/PUT as per API requirements
      res = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderToUse}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN }
      });
      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: `https://gateway.battleface.com/api/partner/orders/${orderToUse}`,
          httpMethod: 'GET',
          httpStatusCode: res.status,
          errorMessage: `Failed to confirm quote: HTTP ${res.status}`,
          requestPayload: null,
          responsePayload: responseText,
          additionalContext: 'confirmQuote function'
        });
        throw new Error('Failed to confirm quote');
      }
      data = await res.json();

      // console.log(JSON.stringify(data));

      setConfirmation(data);
    
      // Extract and store the order number
      const extractedOrderNumber = data.order_number || data.orderNumber || data.id || data.data?.order_number || data.data?.id;
      

      await PaymentConfirmed({
        endpoint: 'payment',
        marketing_channel_code: mcc,
        session_id: sessionId,
        request: extractedOrderNumber,
        response: data,
        status: res?.status,
        timestamp: new Date().toISOString(),
      }, extractedOrderNumber);     

      console.log(data);

      // Wait 3 seconds before fetching policy documents
      await new Promise(resolve => setTimeout(resolve, 3000));

      await GetPolicyDocuments(data.data.items[0].policy_number, data.data.order_number, data.data.quote_id);


      setActiveStep(4);
    } catch (e) {
      setError(e.message);
    } finally {
      // Log the API call regardless of success/failure

      setLoading(false);
    }
  };

  /**
   * STEP 1 → STEP 2: Move from Quote Review to Order Details
   * Purpose: Allow user to proceed to providing applicant information
   * No API call - just UI navigation
   */
  const proceedToOrder = () => {
    // Move from quote review (step 1) to order (step 2)
    setActiveStep(2);
  };

  /**
   * STEP 2 → STEP 3: Create Order with BattleFace
  /**
   * STEP 2 → STEP 3: Create Order with Applicant Details
   * Called when user completes the Order Information form (step 2)
   * Creates a formal order with all applicant details before payment
   * API: POST /api/partner/orders/condensed
   * Input: Quote ID, applicant details, coverage selections
   * Output: Order number for payment processing
   */
  const processOrder = async () => {
    // Validate that we have a quote ID before creating order
    if (!quoteId) {
      setError('No quote ID available. Please get a quote first.');
      return;
    }
    
    setLoading(true); 
    setError('');
    
    try {
      // Build order payload with all collected information
      const orderPayload = {
        quote_id: quoteId,                                             // Quote ID from step 1
        currency_id: "USD",
        product_id: 1303,
        start_at: tripStart ? tripStart.toISOString().split('T')[0] : "2025-06-29",
        end_at: tripEnd ? tripEnd.toISOString().split('T')[0] : "2025-07-04",
        deposit_at: new Date().toISOString().split('T')[0],           // Today's date
        details: [                                                     // Coverage package details
        {
            type: "package",
            product_package_id: tripCostCoverage.toString() +"_limit" // e.g., "2500_limit"
        }
        ],
        applicants: applicants.map((applicant, index) =>              // All traveler details
          cleanApplicantData(applicant, index, travelerAges, tripCostCoverage) // Use selected coverage amount
        ),
        destinations: [                                                // Trip destinations
            {
                country_code: "US"
            }
        ] 
      };

      // Send order creation request to BattleFace API
      const res = await fetch('https://gateway.battleface.com/api/partner/orders/condensed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: BATTLEFACE_API_TOKEN },
        body: JSON.stringify(orderPayload)
      });

      if (!res.ok) {
        // Log the error to our backend for troubleshooting
        const responseText = await res.text();
        await LogBattlefaceError({
          endpoint: 'https://gateway.battleface.com/api/partner/orders/condensed',
          httpMethod: 'POST',
          httpStatusCode: res.status,
          errorMessage: `Failed to create order: HTTP ${res.status}`,
          requestPayload: JSON.stringify(orderPayload),
          responsePayload: responseText,
          additionalContext: 'processOrder function'
        });
        throw new Error('Failed to create order');
      }

      const orderData = await res.json();


      // Extract and store the order number
      const extractedOrderNumber = orderData.order_number || orderData.orderNumber || orderData.id || orderData.data?.order_number || orderData.data?.id;
      //console.log('Extracted Order Number:', extractedOrderNumber);

      await SaveOrder({
        endpoint: 'order',
        quote_id: extractedOrderNumber,
        marketing_channel_code: mcc,
        session_id: sessionId,
        request: extractedOrderNumber,
        response: orderData,
        status: res?.status,
        timestamp: new Date().toISOString(),
      }, extractedOrderNumber);      
      
      if (!extractedOrderNumber) {
        throw new Error('No order number found in API response');
      }

      setOrderNumber(extractedOrderNumber);
      
      // Move to payment step (step 3)
      setActiveStep(3);
      
    } catch (e) {
      console.error('Create Order Error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const processPayment = () => {
    // Move from payment (step 3) to confirmation (step 4)
    // Process payment and confirm quote
    confirmQuote();
  };

  /**
  // Payment Handler Functions

  /**
   * STEP 3 → STEP 4: Handle Successful Payment
   * Called by PaymentForm component after Stripe payment succeeds
   * Actions: Store payment details, call confirmQuote to finalize policy
   * This triggers the final step transition to confirmation/download
   */
  const handlePaymentSuccess = async (paymentIntentResult) => {
    setPaymentIntent(paymentIntentResult);
    
    // After successful payment, confirm the quote to create the final policy
    try {
      await confirmQuote(); // This will move to step 4 (Download/Confirmation)
    } catch (error) {
      setError('Payment succeeded but policy confirmation failed. Please contact support.');
    }
  };

  /**
   * Handle Payment Errors
   * Called by PaymentForm component when payment fails
   * Displays user-friendly error message
   */
  const handlePaymentError = (errorMessage) => {
    setError(`Payment failed: ${errorMessage}`);
  };

  // UI Rendering - The main component render with 5 steps
  return (
  <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box maxWidth={650} mx="auto" boxShadow={3} borderRadius={3} bgcolor="background.paper" overflow="hidden" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Conditional Header Section based on theme */}
          {themeName === THEMES.ORANGE ? (
            /* Orange Theme Header */
            <Box className="orange-header-box" sx={{ 
              background: 'linear-gradient(135deg, #E67E22 0%, #F39C12 100%)',
              color: 'white',
              p: 3,
              position: 'relative',
              textAlign: 'left'
            }}>
              {/* Fireworks overlay behind header (only on download screen) */}
              {showCelebration && (<FireworksOverlay />)}
              {/* Main header content container */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'flex-start' },
                justifyContent: 'space-between',
                gap: { xs: 2, sm: 0 }
              }}>
                {/* Header text */}
                <Box sx={{ flex: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                      Annual (Multi-Trip)<br />
                      Plan Quote
                    </Typography>
                    <Box
                      component="img"
                      src={"https://www.jetsetinsure.com/web/image/855-5ea4b3d6/JetSet%20-%20New%20Tagline.webp"}
                      alt="JetSetInsure.com"
                      sx={{
                        width: { xs: 150, sm: 250 },
                        height: 'auto',
                        borderRadius: 1,
                        ml: 2
                      }}
                    />
                  </Box>
                  <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    ALL OF YOUR TRIPS COVERED<br />
                    UNDER ONE PLAN
                  </Typography>
                  {/* Assistance pill moved 10px below the image */}
                  <Box sx={{
                    mt: '10px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    p: 1.5,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    width: 'fit-content'
                  }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: '#F1C40F' 
                      }} />
                      <Typography variant="caption" fontWeight="bold" sx={{ 
                        fontSize: { xs: '10px', sm: '11px' },
                        lineHeight: 1.2
                      }}>
                        24/7 GLOBAL ASSISTANCE IS INCLUDED<br />
                        WITH ALL PLANS
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            /* Default Theme Header */
            <Box p={3} sx={{ position: 'relative' }}>
              {/* Fireworks overlay behind default header (only on download screen) */}
              {showCelebration && (<FireworksOverlay />)}
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h5" mb={2} fontWeight="bold">
                  Annual / Multi-Trip <br /> Travel Protection
                </Typography>
                <Box
                  component="img"
                  src={"https://www.jetsetinsure.com/web/image/855-5ea4b3d6/JetSet%20-%20New%20Tagline.webp"}
                  alt="JetSetInsure.com"
                  sx={{
                    width: { xs: 90, sm: 130 },
                    height: 'auto',
                    borderRadius: 1,
                    ml: 2
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Learn More Banner under header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#FFF3E0',
            border: '1px solid #E67E22',
            borderRadius: 0,
            px: 2,
            py: 1.5,
            mb: 0,
            cursor: 'pointer',
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            width: '100vw'
          }} onClick={toggleMarketing}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#E67E22', 
                fontWeight: 600,
                textAlign: 'center',
                width: '100%'
              }}
            >
              {showMarketing ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <ArrowBack sx={{ fontSize: 20, color: 'inherit' }} />
                  Go Back To My Quote
                </Box>
              ) : (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <HelpOutline sx={{ fontSize: 20, color: 'inherit' }} />
                  Click here to Learn More
                </Box>
              )}
            </Typography>
          </Box>

          {/* Content Section */}
          <Box p={3}>
            {/* Marketing Channel validation message */}
            {isMccMissing && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">Marketing Channel NOT configured</Typography>
                <Typography variant="body2">Please contact <a href="mailto:support@landsmaninsurance.com">support@landsmaninsurance.com</a>.</Typography>
              </Alert>
            )}
            {/* Section Header - Only show for Orange theme */}
            {activeStep === 0 && themeName === THEMES.ORANGE && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#E67E22', 
                      fontWeight: 'bold',
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    ✈️ ON YOUR MARKS . . .
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Marketing Section */}
            <Box sx={{ display: showMarketing ? 'block' : 'none' }}>
              <Typography variant="h6" mb={2} textAlign="center">Learn More</Typography>
              <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  component="iframe"
                  key={showMarketing ? 'marketing-on' : 'marketing-off'}
                  src={showMarketing 
                    ? 'https://player.vimeo.com/video/1106312192?autoplay=1&muted=1&autopause=0&playsinline=1' 
                    : 'https://player.vimeo.com/video/1106312192'}
                  title="Learn More Video"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                />
              </Box>

              {/* FAQs Section */}
              <Box mt={3}>
                <Typography variant="h6" textAlign="center" fontWeight="bold" mb={2}>
                  Top Questions Answered
                </Typography>
                <Box>
                  {/* Updated FAQs based on provided HTML */}
                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        What is an Annual Travel Plan?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        An Annual Multi-Trip Plan is a comprehensive package of coverages perfect for those who travel spontaneously or take multiple trips per year. All trips up to 40 days are covered when 100 miles or more from home, domestic or international.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        Who is the Annual Multi-Trip Plan best suited for?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        It’s the perfect, budget-friendly plan for people who like to travel frequently or spontaneously. The Annual Multi-Trip Plan offers a full range of benefits, including:
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        <li><Typography variant="body2">A comprehensive range of coverages</Typography></li>
                        <li><Typography variant="body2">Coverage for sickness due to COVID-19</Typography></li>
                        <li><Typography variant="body2">Available coverage for Pre-Existing Medical Conditions</Typography></li>
                        <li><Typography variant="body2">Coverage for Adventure Activities</Typography></li>
                        <li><Typography variant="body2">Worldwide Travel Assistance</Typography></li>
                      </Box>
                      <Typography variant="body2" mt={2}>
                        Limitations and exclusions apply. Please review{' '}
                        <Box component="a" href="https://library.battleface.com/" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>sample policy details</Box>{' '}in your state of residence for full terms, conditions, and exclusions of coverage. See the{' '}
                        <Box component="a" href="https://partner.battleface.com/library/landsman-elite-multi-annual" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>Customer Library</Box>{' '}for the plan language applicable to your state.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        How many trips per year am I covered for?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        There is no limit to the number of trips you can take each 365-day period. All trips up to 40 days are covered when 100 miles or more from home, domestic or international.
                      </Typography>
                      <Typography variant="body2">
                        If you are on a covered trip that extends beyond the end of the plan term, any benefits that apply during that covered trip expire at the end of the plan term.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        What is the maximum covered trip length for any one trip during the Multi-Trip Annual plan?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        The maximum covered length for any one trip is 40 days. Any trip greater than 40 days is not considered a "covered trip" as defined by the policy. For example, a 60-day trip would not be covered, and coverage cannot be applied to only part of that trip.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        Do I need to notify the insurance carrier when I go on a trip?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        No, there's no need to notify the insurance carrier. Your battleface plan is valid the entire 12 months.
                      </Typography>
                      <Typography variant="body2"><strong>Travel fearlessly. Travel farther. Travel free.</strong></Typography>
                      <Typography variant="body2">That's the <strong>JetSetInsure.com</strong> way!</Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        Can I pick a future date on which to start my plan?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        Yes! You may select a start date for your plan up to 180 days into the future.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        Is the annual Multi-Trip plan valid for travel anywhere in the world?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        Pretty much! There are a few countries and regions where we can’t offer coverage or services. These can change from time to time. Please{' '}
                        <Box component="a" href="https://library.battleface.com/ineligible-destinations/" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>click here</Box>{' '}to view the most recent list of ineligible countries.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        What is considered a Pre-Existing Medical Condition, and can I get coverage for it on the Multi-Trip Annual Plan?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        If you, a family member, or a traveling companion have a pre-existing medical condition, many travel protection plans exclude any losses resulting from that condition. However, under the Annual Multi-Trip plan, pre-existing medical conditions are covered after your insurance plan has been in effect for at least sixty (60) days.
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        Any pre-existing medical conditions will be covered if you apply for a new plan effective immediately after the end of the annual term. If you apply at a later date, pre-existing medical conditions will not be covered until the new plan has been in effect for at least sixty (60) days.
                      </Typography>
                      <Typography variant="body2">
                        <strong>Pre-existing medical condition</strong> means an injury, sickness, death or other condition of you, your traveling companion, family member, host at destination, business partner, pet, or service animal, for which medical advice, diagnosis, care or treatment was recommended by or received from a physician within the one hundred eighty (180) day period immediately preceding and including the purchase date of a plan.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        How does the Trip Cancellation limit work for the Annual Multi-Trip plan? Is the limit per-trip, or for the entire year? What about the other benefits?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        The Trip Cancellation benefit is effective for the entire duration of the annual plan term. This benefit will end when the plan term ends, or when the benefit is fully exhausted, whichever comes earlier.
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        All other benefits listed in the policy, apart from Trip Cancellation, are effective on the date and time you begin each covered trip during the plan term. These benefits conclude at the earliest of the following: the date and time you arrive at your return destination (or the destination for one-way travel), the date the last benefit is fully exhausted, or the end of the plan term. These other benefits include:
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        <li><Typography variant="body2">Single Occupancy</Typography></li>
                        <li><Typography variant="body2">Trip Interruption</Typography></li>
                        <li><Typography variant="body2">Trip Delay</Typography></li>
                        <li><Typography variant="body2">Missed Connection</Typography></li>
                        <li><Typography variant="body2">Baggage and Personal Effects</Typography></li>
                        <li><Typography variant="body2">Baggage Delay</Typography></li>
                        <li><Typography variant="body2">Travel Medical Expense</Typography></li>
                        <li><Typography variant="body2">Emergency Evacuation and Repatriation of Remains</Typography></li>
                      </Box>
                      <Typography variant="body2" mt={1}>
                        A "covered trip" is defined as a period of round-trip travel to a destination at least one hundred (100) miles from your primary residence, with a maximum of 40 days in length.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        What’s the difference between Trip Cancellation, Trip Interruption and Trip Delay?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        Trip Cancellation covers you <strong><em>before</em></strong> your trip starts, in the event you have to cancel your trip for a covered reason.
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        Trip Interruption covers you <strong><em>during</em></strong> your trip, in the event you have to return home due to a covered reason. You can be reimbursed for the cost of your unused trip arrangements, <strong>plus</strong> any extra airfare costs to return home.
                      </Typography>
                      <Typography variant="body2">
                        Trip Delay also covers you <strong><em>during</em></strong> your trip— it can reimburse the costs of meals, local transportation, and accommodations when your trip is unexpectedly delayed for covered reasons.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        Does an Annual Multi-Trip plan include travel assistance services?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        Yes, the Annual Multi-Trip Plan includes both Emergency Assistance and general Travel Assistance. Non-Insurance Assistance services are provided by Robin Assist.
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        Robin Assist is battleface's brand name for providing 24/7 responsive customer service, claims, emergency travel and medical assistance from any device, any time, any place.
                      </Typography>
                      <Typography variant="body2" mb={1}><strong>Emergency Medical Assistance</strong> services include:</Typography>
                      <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        <li><Typography variant="body2">Medical referrals</Typography></li>
                        <li><Typography variant="body2">Medical case management</Typography></li>
                        <li><Typography variant="body2">Dental/vision/hospital referrals</Typography></li>
                        <li><Typography variant="body2">Medical payment arrangements</Typography></li>
                        <li><Typography variant="body2">Emergency Rx assistance</Typography></li>
                      </Box>
                      <Typography variant="body2" mt={2}><strong>Travel Assistance Services</strong> include:</Typography>
                      <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        <li><Typography variant="body2">Emergency travel arrangements</Typography></li>
                        <li><Typography variant="body2">Embassy/consulate information</Typography></li>
                        <li><Typography variant="body2">Missed connections coordination</Typography></li>
                        <li><Typography variant="body2">Lost baggage search</Typography></li>
                        <li><Typography variant="body2">Lost passport assistance</Typography></li>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        “Here’s my situation…will it be covered?”
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        It’s hard to give definitive answers to hypothetical situations because one small detail can change everything. That said, battleface will do their best to answer your questions, or help you understand the plan language and how it might apply to various situations.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        Are there any exclusions?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        Travel protection doesn’t cover everything. It is designed to provide coverage for unforeseeable circumstances. This information is a brief summary and does not include all terms, conditions, limitations, exclusions, and termination provisions of the plans described. Coverage may not be the same or available for residents of all states. For a full description of coverage, please carefully read your plan wording (see the{' '}
                        <Box component="a" href="https://partner.battleface.com/library/landsman-elite-multi-annual" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>Customer Library</Box>{' '}for your state) or connect with a battleface representative to discuss.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        Can I cancel the policy I purchase for a full refund?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        <strong>Review your policy with confidence.</strong> If you decide it’s not the right fit, you can cancel within 10 days for a full refund—no questions asked. This refund is only available if the covered trip has not started and if a claim has not been initiated. After this ten (10)-day period, your premium is non-refundable.
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        Travel with enhanced peace of mind, knowing you're covered from the moment you depart until the moment you return.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ color: '#B56308', fontWeight: 600 }}>
                        How do claims work?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" mb={1}>
                        Our partner battleface makes the claims process as smooth as possible with fast, responsive support. You can file and track claims online anytime, and you'll have a dedicated claims examiner to answer your questions and guide you through every step.
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>How do I file a claim? What can I do to help with processing?</strong>
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        If something happens before or during your trip and you want to use your travel protection, you’ll need to file a claim to be reimbursed. We recommend bringing a print-out of your plan documents with you when traveling.
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        For non-emergency situations before or during your trip: document the situation as much as possible and keep receipts for any expenses incurred. If you have any questions about your situation, or you need guidance, please connect with battleface for a chat.
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        For emergency situations, or if you need assistance during your trip: battleface can help you with any situation and answer questions about next steps and required documentation.
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        Filing a claim starts with completing a claim form. The claim form helps you:
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        <li><Typography variant="body2">Inform battleface about the incident that caused your claim</Typography></li>
                        <li><Typography variant="body2">Specify the financial loss you want reimbursed</Typography></li>
                        <li><Typography variant="body2">Supply evidence of the incident to support your claim</Typography></li>
                      </Box>
                      <Typography variant="body2" mt={1}>
                        Once submitted, your claim is assessed by a claims analyst who determines whether it meets the terms of your plan and communicates the outcome to you.{' '}
                        <Box component="a" href="https://www.battleface.com/en-us/file-a-claim/" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>
                          Click here
                        </Box>{' '}to start your claim.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>

                {/* Footer Section: JetSetInsure & battleface disclosures */}
                <Box
                  mt={4}
                  sx={{
                    backgroundColor: '#2C1E15',
                    color: '#f2e6d8',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    boxShadow: 1,
                    '& .MuiTypography-root': { color: '#f2e6d8' }
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                    <Box
                      component="img"
                      src={"https://www.jetsetinsure.com/web/image/855-5ea4b3d6/JetSet%20-%20New%20Tagline.webp"}
                      alt="JetSetInsure.com"
                      sx={{
                        width: 140,
                        height: 'auto',
                        borderRadius: 1,
                        display: 'block'
                      }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        JetSetInsure.com™ is wholly owned by Landsman Insurance Services, LLC. National Producer Number: 21500715 and connects travelers with top-rated travel insurance products from leading third-party insurers. JetSetInsure.com does not underwrite insurance policies or provide claims service. Coverage decisions and claims are handled directly by the issuing insurance companies.
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        "Annual Policies" and "Single Trip Policies" are provided by battleface Insurance Services, LLC.
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        battleface Travel Insurance plans are underwritten by Spinnaker Insurance Company (an IL Corporation, NAIC# 24376), with administrative office at One Pluckemin Way, Suite 102, Bedminster, NJ 07921. Plans are offered by Landsman Insurance Services, LLC. NPN: 21500715 (California: Landsman Insurance Services LLC LICENSE NUMBER: 6016307) (Texas: Landsman Insurance Services LICENSE NUMBER: 3312498) and administered by battleface Insurance Services LLC, 800 N. High Street, Columbus, OH 43215, National Producer Number 18731960 (FL License number L107363/CA License number 0M75381). Plans available for purchase through <Box component="span" sx={{ fontWeight: 700 }}>JetSetInsure.com™</Box>
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        This is a brief description of the coverage provided under policy series RIG-1000. The Policy will contain reductions, limitations, exclusions, and termination
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 2 }}>
                        Non-insurance assistance services are provided by Robin Assist LLC (in CA: battleface Insurance Services dba Robin Assist).
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 3, opacity: 0.9 }}>
                        Copyright © 2025 Landsman Insurance Services, LLC. NPN: 21500715 - All rights reserved.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Main quote content hidden when marketing visible */}
            <Box sx={{ display: showMarketing ? 'none' : 'block' }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box display="flex" justifyContent="center" my={2}><CircularProgress /></Box>}

        {/* STEP 0: DETAILS - Collect basic trip and traveler information 
             This step gathers the minimum information needed to get a quote:
             - Trip start date (auto-calculates end date to 364 days later)
             - Home state (affects pricing and regulations)
             - Number of travelers (1-10)
             - Age of each traveler (affects pricing)
             
             When submitted, calls createQuote() API function */}
        {activeStep === 0 && (
          <form onSubmit={e => { e.preventDefault(); createQuote(); }}>
            {/* Trip Start Date Selection */}
            <Box mb={3}>
              <Typography variant="h6" mb={2}>When would you like your plan to begin?</Typography>              
              <DatePicker
                value={tripStart}
                onChange={(newDate) => {
                  setTripStart(newDate);
                  // Automatically set trip end date to 364 days after start date (max policy length)
                  if (newDate) {
                    const endDate = new Date(newDate);
                    endDate.setDate(endDate.getDate() + 364);
                    setTripEnd(endDate);
                  }
                }}
                shouldDisableDate={(date) => {
                  // Only allow dates from tomorrow to 180 days from today
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  tomorrow.setHours(0, 0, 0, 0);
                  const maxDate = new Date();
                  maxDate.setDate(maxDate.getDate() + 180);
                  return date < tomorrow || date > maxDate;
                }}
                defaultCalendarMonth={findFirstAllowableDate()}
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    margin: 'normal', 
                    error: !!errors.tripStart, 
                    helperText: errors.tripStart || "You can choose for your plan to begin on a future date as far as 180 days from tomorrow.",
                    placeholder: "MM/DD/YYYY"
                  } 
                }}
              />
            </Box>

            {/* Home State Selection - Required for pricing and regulations */}
            <TextField
              select
              label="Home State"
              value={homeCountryState}
              onChange={(e) => {
                const stateCode = e.target.value;
                
                // Check if the selected state is ineligible
                if (stateCode === 'WA' || stateCode === 'NY') {
                  setSelectedIneligibleState(
                    stateOptions.find(state => state.code === stateCode)?.name || stateCode
                  );
                  setStateDialogOpen(true);
                } else {
                  setHomeCountryState(stateCode);
                }
              }}
              fullWidth
              margin="normal"
              error={!!errors.homeCountryState}
              helperText={errors.homeCountryState || "Select your home state"}
            >
              {stateOptions.map((state) => (
                <MenuItem key={state.code} value={state.code}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>            

            {/* Number of Travelers - Affects pricing */}
            <TextField
              label="Number of Travelers" type="number" fullWidth margin="normal"
              inputProps={{ min: 1, max: 10 }}
              value={numTravelers} onChange={handleNumTravelersChange}
              error={!!errors.numTravelers} helperText={errors.numTravelers}
            />
            
            {/* Individual Traveler Ages - Required for pricing calculations */}
            <Box mt={2}>
              <Typography variant="subtitle1">Traveler Ages</Typography>
              {Array.from({ length: numTravelers }).map((_, idx) => (
                <TextField
                  key={idx}
                  label={`Traveler ${idx + 1} Age`}
                  type="number"
                  inputProps={{ min: 0, max: 120 }}
                  value={travelerAges[idx] || ''}
                  onChange={e => handleAgeChange(idx, e.target.value)}
                  margin="dense"
                  fullWidth
                  error={!!errors[`age${idx}`]}
                  helperText={errors[`age${idx}`]}
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
            
            {/* Submit to get quote from BattleFace API */}
            <Button
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              disabled={loading || Object.keys(errors).length > 0}
              sx={themeName === THEMES.ORANGE ? { 
                mt: 3, 
                py: 2,
                fontSize: '16px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #E67E22 0%, #F39C12 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #D35400 0%, #E67E22 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0px 4px 12px rgba(230, 126, 34, 0.4)',
                },
                '&:disabled': {
                  background: '#BDC3C7',
                  color: '#7F8C8D',
                }
              } : { mt: 2 }}
            >
              {themeName === THEMES.ORANGE ? '📋 GET INSTANT QUOTE' : 'Get Quote'}
            </Button>
          </form>
        )}

        {/* STEP 1: QUOTE REVIEW - Display quote results and coverage options
             This step shows the quote returned from BattleFace API including:
             - Total policy cost prominently displayed
             - Detailed breakdown of all coverage benefits and limits
             - Trip cancellation coverage selector (dropdown with 5 options)
             - Options to proceed to order or go back to edit details
             
             The coverage dropdown triggers requoteWithNewCoverage() when changed */}
        {activeStep === 1 && quote && (
          <Box>
            {/* Policy Amount Section */}
            <Box mb={4} p={3} border={1} borderColor="primary.main" borderRadius={2} bgcolor="primary.50">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Total Plan Cost:
                </Typography>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {quote.data?.currency_id || 'USD'} ${quote.data?.quoted_price?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Box>
                  {quote.data?.trip_length && (
                    <Typography variant="body2" color="text.secondary">
                      Coverage for {quote.data.trip_length} days
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography 
                    component="a" 
                    href="https://partner.battleface.com/library/landsman-elite-multi-annual" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    variant="body2" 
                    color="primary.main" 
                    sx={{ 
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'none'
                      }
                    }}
                  >
                    View Sample Plan
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="h6" mb={3} textAlign="center">Included in your plan</Typography>
            
            {/* Benefits Section */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="primary">Benefits</Typography>
                <Typography variant="body2" color="text.secondary">
                  Maximum Limits<br />
                  <span style={{ fontSize: '0.75rem' }}>per plan term</span>
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Trip Cancellation */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>Trip Cancellation</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Coverage for your pre-paid, non-refundable trip cost and some related expenses if you need to cancel a trip for a covered reason.
                </Typography>
                
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Choose a coverage limit</Typography>
                <TextField
                  select
                  value={tripCostCoverage}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    requoteWithNewCoverage(newValue);
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value={2500}>$2,500</MenuItem>
                  <MenuItem value={5000}>$5,000</MenuItem>
                  <MenuItem value={10000}>$10,000</MenuItem>
                  <MenuItem value={20000}>$20,000</MenuItem>
                  <MenuItem value={30000}>$30,000</MenuItem>
                </TextField>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Trip Interruption */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Trip Interruption</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Coverage in the event a portion of a trip is missed, or if you must return home early due to unforeseen circumstances, including illness, injury, or death.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    150% of Trip Cancellation Limit
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Single Occupancy */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Single Occupancy</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Coverage for your additional costs if your traveling companion cancels or interrupts their trip for a covered reason and you choose to still go.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    Up to ${tripCostCoverage.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Trip Delay */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Trip Delay</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Protection against expenses incurred as a result of travel delay for a defined period of time. Examples include unexpected hotel, meal, or transportation costs.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    $200/day up to $2,000
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Missed Connection */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Missed Connection</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Can reimburse you for the cost of extra transportation to rejoin the trip, additional local expenses, and the cost of missed arrangements.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    $1,000
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Baggage Loss */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Baggage Loss</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Coverage for reimbursement of lost, stolen, or damaged luggage, sporting equipment and personal effects.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    $500/item up to $2,000
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Baggage Delay */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Baggage Delay</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reimburses the cost of clothing, toiletries, and other essential items if your baggage is delayed for at least 12 hours.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    $100/day up to $1,000
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Travel Medical */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Travel Medical</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Coverage for the costs to treat a medical emergency, including COVID-19, during a trip.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    {getTravelMedicalCoverage(homeCountryState).amount}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Emergency Dental */}
              <Box mb={2} ml={4}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="bold">Emergency Dental</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Coverage for emergency dental care while on your trip.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    $750
                  </Typography>
                </Box>
              </Box>
              
              {/* Hospital Room & Board */}
              <Box mb={2} ml={4}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="bold">Hospital Room & Board</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Can reimburse up to the daily amount shown for hospital room & board.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    unlimited
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Emergency Evacuation */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Emergency Evacuation</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Coverage to transport you to the nearest adequate medical facility, or home, in the event of a medical emergency during a covered trip. Primary coverage, plus includes coverage for Adventure Activities.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    $500,000
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Medical & Travel Assistance Services */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Medical & Travel Assistance Services</Typography>
                    <Typography variant="body2" color="text.secondary">
                      The purchase of an Annual Multi-Trip Plan includes both Emergency Assistance and general Travel 
                      Assistance. Non Insurance Assistance services are provided by Robin Assist. Robin Assist is 
                      battleface's brand name for providing 24/7 responsive customer service, claims, emergency travel 
                      and medical assistance from any device, any time, any place. Their operations team consists of 
                      multi-lingual talented humans who come from extensive travel protection service backgrounds. 
                      In the event you become sick or injured while traveling, services provided with your plan can help. 
                      battleface will get you started with a referral to a local licensed medical professional. They will 
                      monitor your condition and, if medically necessary, make arrangements to transport you to the 
                      nearest medical facility that can properly treat you. With Emergency Evacuation provided under 
                      the Annual Multi-Trip plan, they can even pay eligible medical transportation costs up front, 
                      including getting you home. Please review the FAQ section for additional services provided.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" minWidth="120px" textAlign="right">
                    Included
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box mt={3}>
              <Button variant="contained" color="primary" fullWidth onClick={proceedToOrder} disabled={loading || isMccMissing}>
                Proceed to Order
              </Button>
              <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => {
                guardedSetActiveStep(0);
              }} disabled={isMccMissing}>
                Back to Edit Details
              </Button>
            </Box>
          </Box>
        )}

        {/* STEP 2: ORDER INFORMATION - Collect personal details for all travelers
             This step gathers detailed personal information required for the insurance policy:
             - Personal details for each traveler (name, email, phone, address)
             - Email validation with comprehensive format checking
             - Phone number formatting (auto-formats as user types)
             - State automatically set from step 0 selection
             - Validation ensures all required fields are completed correctly
             
             When submitted, calls processOrder() to create order with BattleFace */}
        {activeStep === 2 && (
          <Box>
            {/* Order Summary Box */}
            <Box mb={4} p={3} border={2} borderColor="primary.main" borderRadius={2} bgcolor="primary.50">
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Your one-time payment
                  </Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {quote?.data?.currency_id || 'USD'} ${quote?.data?.quoted_price?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => setActiveStep(0)}
                  sx={{ textTransform: 'none' }}
                >
                  EDIT
                </Button>
              </Box>
              
              <Box display="flex" gap={4} mt={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Plan Start
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {tripStart ? tripStart.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    }) : 'Not selected'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Plan End
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {tripEnd ? tripEnd.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    }) : 'Not selected'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Travelers
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {numTravelers} {numTravelers === 1 ? 'traveler' : 'travelers'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="h6" mb={2}>Order Information</Typography>
            <Typography variant="body2" mb={3} color="text.secondary">
              Please provide details for all travelers to complete your order.
            </Typography>

            {applicants.map((applicant, index) => (
              <Box key={index} mb={4} p={2} border={1} borderColor="grey.300" borderRadius={1}>
                <Typography variant="subtitle1" mb={2}>
                  Traveler {index + 1} {applicant.is_main && '(Main Applicant)'}
                </Typography>
                
                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="First Name"
                    value={applicant.first_name}
                    onChange={(e) => handleApplicantChange(index, 'first_name', e.target.value)}
                    fullWidth
                    error={!!errors[`applicant${index}_first_name`]}
                    helperText={errors[`applicant${index}_first_name`]}
                  />
                  <TextField
                    label="Middle Name"
                    value={applicant.middle_name}
                    onChange={(e) => handleApplicantChange(index, 'middle_name', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Last Name"
                    value={applicant.last_name}
                    onChange={(e) => handleApplicantChange(index, 'last_name', e.target.value)}
                    fullWidth
                    error={!!errors[`applicant${index}_last_name`]}
                    helperText={errors[`applicant${index}_last_name`]}
                  />
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="Email"
                    type="email"
                    value={applicant.email}
                    onChange={(e) => handleApplicantChange(index, 'email', e.target.value)}
                    fullWidth
                    placeholder="example@domain.com"
                    error={!!errors[`applicant${index}_email`]}
                    helperText={errors[`applicant${index}_email`] || "Enter a valid email address"}
                  />
                  <TextField
                    label="Contact Number"
                    value={applicant.contact_number}
                    onChange={(e) => handleApplicantChange(index, 'contact_number', e.target.value)}
                    fullWidth
                    placeholder="(555) 123-4567"
                    error={!!errors[`applicant${index}_contact_number`]}
                    helperText={errors[`applicant${index}_contact_number`] || "10-digit phone number required"}
                  />
                </Box>

                <TextField
                  label="Address"
                  value={applicant.address}
                  onChange={(e) => handleApplicantChange(index, 'address', e.target.value)}
                  fullWidth
                  margin="normal"
                />

                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="City"
                    value={applicant.city}
                    onChange={(e) => handleApplicantChange(index, 'city', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Postal Code"
                    value={applicant.post_code}
                    onChange={(e) => handleApplicantChange(index, 'post_code', e.target.value)}
                    fullWidth
                    error={!!errors[`applicant${index}_post_code`]}
                    helperText={errors[`applicant${index}_post_code`]}
                  />
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    select
                    label="Country"
                    value={applicant.country_code}
                    onChange={(e) => handleApplicantChange(index, 'country_code', e.target.value)}
                    fullWidth
                  >
                    {COUNTRY_LIST.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="State"
                    value={homeCountryState}
                    fullWidth
                    disabled
                    helperText="State automatically set from Policy Details"
                  >
                    {stateOptions.map((state) => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            ))}

            <Box mt={3}>
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                onClick={processOrder} 
                disabled={loading || Object.keys(errors).length > 0 || isMccMissing}
              >
                Proceed to Payment
              </Button>
              <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => guardedSetActiveStep(1)} disabled={isMccMissing}>
                Back to Quote Review
              </Button>
            </Box>
          </Box>
        )}

        {/* STEP 3: PAYMENT - Process payment through Stripe/BattleFace integration
             This step handles the payment processing flow:
             - Displays order summary with final pricing
             - Shows order number from step 2 (if available)
             - Renders Stripe payment form (wrapped in Elements provider)
             - Payment flow follows BattleFace/Stripe integration diagram:
               1. Create PaymentMethod with Stripe using card details
               2. Fetch PaymentIntent from BattleFace (GET request)
               3. Use client_secret to confirm payment with Stripe
               4. Handle success/error and proceed to confirmation
             
             On success, calls handlePaymentSuccess() → confirmQuote() → step 4 */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" mb={2}>Payment Information</Typography>
            {orderNumber && (
              <Box mb={3} p={2} bgcolor="success.50" border={1} borderColor="success.main" borderRadius={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" color="success.dark" sx={{ flexGrow: 1 }}>
                    Order Number: <strong>{orderNumber}</strong>
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenEmailDialog}
                    disabled={emailing}
                  >
                    Email
                  </Button>
                </Box>
              </Box>
            )}
            <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog}>
              <DialogTitle>Email Quote</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {`Would you like to email the quote to ${(applicants.find(a => a.is_main) || applicants[0])?.email || 'the applicant'}?`}
                </DialogContentText>
                {!((applicants.find(a => a.is_main) || applicants[0])?.email) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No applicant email is set. Please enter an email in the applicant details first.
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseEmailDialog} disabled={emailing}>Cancel</Button>
                <Button
                  onClick={handleConfirmEmail}
                  variant="contained"
                  disabled={emailing || !((applicants.find(a => a.is_main) || applicants[0])?.email)}
                >
                  {emailing ? 'Sending...' : 'Yes'}
                </Button>
              </DialogActions>
            </Dialog>
            {/* Global Snackbar for email feedback */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                sx={{ width: '100%' }}
                variant="filled"
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
            <Typography variant="body2" mb={3} color="text.secondary">
              Review your order and provide payment details to complete your purchase.
            </Typography>

            {/* Order Summary */}
            <Box mb={4} p={3} border={1} borderColor="grey.300" borderRadius={2} bgcolor="grey.50">
              <Typography variant="h6" mb={2}>Order Summary</Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Trip Cancellation Limit:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {quote?.data?.currency_id || 'USD'} ${tripCostCoverage?.toLocaleString()}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Number of Travelers:</Typography>
                <Typography variant="body1">{numTravelers}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Plan Start Date:</Typography>
                <Typography variant="body1">
                  {tripStart ? tripStart.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: '2-digit', 
                    day: '2-digit' 
                  }) : 'Not selected'}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {quote?.data?.currency_id || 'USD'} ${quote?.data?.quoted_price?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
            </Box>

            {/* Stripe Payment Form */}
            <Elements stripe={stripePromise}>
              <PaymentForm
                quote={quote}
                tripCostCoverage={tripCostCoverage}
                numTravelers={numTravelers}
                orderNumber={orderNumber}
                applicants={applicants}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                loading={loading}
                setLoading={setLoading}
              />
            </Elements>

            <Box mt={3}>
              <Button variant="outlined" fullWidth onClick={() => guardedSetActiveStep(2)} disabled={isMccMissing}>
                Back to Order Information
              </Button>
            </Box>
          </Box>
        )}

        {/* STEP 4: CONFIRMATION/DOWNLOAD - Display policy confirmation and details
             This is the final step showing successful completion:
             - Success message confirming purchase
             - Detailed policy information (insured name, policy dates, premium)
             - Payment details (if available from Stripe)
             - Policy number from BattleFace confirmation
             - Order number for reference
             - Download policy button
             - Information about policy documents being emailed
             - Option to start a new quote
             
             This step is reached automatically after successful payment and confirmQuote() call */}
        {activeStep === 4 && confirmation && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6" component="div" fontWeight="bold">
                🎉 Purchase Confirmed!
              </Typography>
              <Typography variant="body2" mt={1}>
                Your travel insurance policy has been successfully purchased and is now active.
              </Typography>
            </Alert>
            
            {/* Policy Details Card */}
            <Box mb={4} p={3} border={2} borderColor="primary.main" borderRadius={2} bgcolor="primary.50">
              <Typography variant="h6" fontWeight="bold" color="primary.main" mb={3} textAlign="center">
                Plan Details
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Insured Name */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Insured Name:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {applicants[0]?.first_name} {applicants[0]?.last_name}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Policy Number */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Policy Number:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {confirmation.data.items[0].policy_number}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Purchase Date */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Purchase Date:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    })}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Policy Start Date */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Plan Start Date:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {tripStart ? tripStart.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Not available'}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Policy End Date */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Plan End Date:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {tripEnd ? tripEnd.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Not available'}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Premium Paid */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Premium Paid:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {quote?.data?.currency_id || 'USD'} ${quote?.data?.quoted_price?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Coverage Amount */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="medium">Annual Trip Cancellation Limit:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ${tripCostCoverage?.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Payment Success Info */}
            {paymentIntent && (
              <Box mb={3} p={2} bgcolor="success.50" border={1} borderColor="success.main" borderRadius={1}>
                <Typography variant="body2" color="success.dark">
                  <strong>✅ Payment Successful</strong><br />
                  Payment ID: {paymentIntent.id}<br />
                  Amount: ${(paymentIntent.amount / 100).toFixed(2)} {paymentIntent.currency.toUpperCase()}
                </Typography>
              </Box>
            )}
            
            {/* Order Number */}
            {orderNumber && (
              <Box mb={3} p={2} bgcolor="info.50" border={1} borderColor="info.main" borderRadius={1}>
                <Typography variant="body2" color="info.dark">
                  <strong>Order Reference:</strong> {orderNumber}
                </Typography>
              </Box>
            )}

            {/* Download Policy Button */}
            <Box mt={3} mb={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={async () => {
                  try {
                    const policyNumber = confirmation.data.items[0].policy_number;
                    // console.log('Attempting to get policy file URL for policy:', policyNumber, 'order:', orderNumber);
                    
                    // Call the new API endpoint to get the file URL
                    const response = await fetch(`https://gateway.battleface.com/api/partner/orders/${orderNumber}/policies/${policyNumber}/files`, {
                      method: 'GET',
                      headers: {
                        'Authorization': BATTLEFACE_API_TOKEN
                      }
                    });

                    // console.log('Files API response status:', response.status);

                    if (!response.ok) {
                      throw new Error(`Failed to get policy file URL (Status: ${response.status}). Policy files may not be ready yet.`);
                    }

                    const data = await response.json();
                    // console.log('Files API response data:', data);

                    // Check if we have a valid response with file data
                    if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
                      throw new Error('No policy files found in response.');
                    }

                    // Get the first file's URL (assuming the policy file is the first one)
                    const fileUrl = data.data[0].url;
                    if (!fileUrl) {
                      throw new Error('No download URL found in file data.');
                    }

                    // console.log('Using file URL for download:', fileUrl);

                    // Fetch the actual file using the provided URL
                    const fileResponse = await fetch(fileUrl, {
                      method: 'GET',
                      headers: {
                        'Authorization': BATTLEFACE_API_TOKEN
                      }
                    });

                    if (!fileResponse.ok) {
                      throw new Error(`Failed to download file from URL (Status: ${fileResponse.status}).`);
                    }

                    // Create a blob from the file response and trigger download
                    const blob = await fileResponse.blob();
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = `policy-${policyNumber}.pdf`; // Use the file name from response if available
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(downloadUrl);
                  } catch (error) {
                    console.error('Policy download error:', error);
                    setError(`Failed to download policy: ${error.message}. Please try again later or contact support.`);
                  }
                }}
                sx={{ fontWeight: 600 }}
              >
                Download Policy
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" mt={2}>
              A confirmation email will be sent to your registered email address with your policy documents.
            </Typography>
            
            {/* Add more confirmation details as needed */}
            <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={() => window.location.reload()}>
              Start New Quote
            </Button>
          </Box>
        )}
            </Box>{/* end main content toggle */}
          </Box>
        </Box>
        
        {/* Dialog for ineligible states (Washington and New York) */}
        <Dialog
          open={stateDialogOpen}
          onClose={() => setStateDialogOpen(false)}
          aria-labelledby="ineligible-state-dialog-title"
          aria-describedby="ineligible-state-dialog-description"
        >
          <DialogTitle id="ineligible-state-dialog-title">
            Coverage Not Available
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="ineligible-state-dialog-description">
              We're sorry, but this coverage is not available for residents of {selectedIneligibleState}.
              <Box mt={2}>
                For more information about coverage exclusions and ineligible destinations, please click here:
              </Box>
              <Box 
                component="a" 
                href="https://library.battleface.com/ineligible-destinations/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'block', 
                  mt: 2, 
                  color: 'primary.main',
                  fontWeight: 'medium',
                  textDecoration: 'underline'
                }}
              >
                Ineligible Destinations
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStateDialogOpen(false)} color="primary" autoFocus>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </ThemeProvider>
  );
}